// Developer View as (2026-10-21): the dashboard as one customer login sees it, read-only. Replaces the old token
// impersonation on the dashboard side. The API holds the session (users/{developer}.viewAs, logged) and enforces
// read-only; this tab remembers which login it shows (utils/viewAs.js). Start from Developer > See menus as a user.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import axiosInstance, { clearApiCache } from '../services/api';
import { viewAsSession, rememberViewAs, forgetViewAs } from '../utils/viewAs';

const ImpersonationContext = createContext();

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within ImpersonationProvider');
  }
  return context;
}

const OWN_REQUEST = { skipViewAs: true, skipCompanyScope: true, skipAutoLocation: true };

// Clears cached data but keeps this tab's server session id, like a company switch (prepareCompanySwitch): without it
// the company switcher never loads (it waits for a session id).
function clearCacheKeepSession() {
  const sessionId = sessionStorage.getItem('sessionId');
  clearApiCache();
  if (sessionId) sessionStorage.setItem('sessionId', sessionId);
}

export function ImpersonationProvider({ children }) {
  const { currentUser, userRole } = useAuth();
  const [session] = useState(() => viewAsSession());
  const [now, setNow] = useState(() => Date.now());
  // A session left on in another tab keeps the developer's own login read-only: offered here so it can be ended.
  const [elsewhere, setElsewhere] = useState(null);

  useEffect(() => {
    if (!session) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, [session]);

  useEffect(() => {
    if (session || !currentUser || userRole !== 'developer') return undefined;
    let cancelled = false;
    axiosInstance.get('/developer/view-as/current', OWN_REQUEST)
      .then(({ data }) => { if (!cancelled) setElsewhere(data?.active ? data : null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [session, currentUser, userRole]);

  const startViewAs = useCallback(async (uid) => {
    const { data } = await axiosInstance.post('/developer/view-as/start', { uid }, OWN_REQUEST);
    clearCacheKeepSession();
    rememberViewAs(data);
    window.location.assign('/dashboard');
  }, []);

  const stopImpersonation = useCallback(async () => {
    try { await axiosInstance.post('/developer/view-as/stop', {}, OWN_REQUEST); }
    catch (error) { console.error('[View as] Could not end the session (it runs out by itself):', error); }
    forgetViewAs();
    clearCacheKeepSession();
    window.location.assign('/developer');
  }, []);

  const expired = session && session.expiresAt <= now;
  useEffect(() => { if (expired) stopImpersonation(); }, [expired, stopImpersonation]);

  const value = useMemo(() => ({
    isImpersonating: Boolean(session),
    targetUser: session,
    timeRemaining: session ? Math.max(0, Math.floor((session.expiresAt - now) / 1000)) : null,
    startViewAs,
    stopImpersonation,
    sessionElsewhere: elsewhere
  }), [session, now, startViewAs, stopImpersonation, elsewhere]);

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export default ImpersonationContext;
